'use client';

import { usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { getLocaleFlagIcon } from '@/helpers/cdn';
import { countryTitle } from '@/helpers/languages';
import { getLanguageByCountry } from '@/helpers/transl';
import { useLanguageChange } from '@/modules/localization';
import { useCommonStore } from '@/store';

import { ScLocalePopup } from './styled';
import { Spinner } from '@/styles/common';

export type CheckLocaleProps = {
  onClose: () => void;
};

export const CheckLocale: FC<CheckLocaleProps> = ({ onClose }) => {
  const {
    t,
    i18n: { language: currentLang },
  } = useTranslation('common');
  const { mainColor, whiteColor } = useTheme();
  const pathname = usePathname();
  const { push } = useRouter();

  const [changeLanguage, pending] = useLanguageChange();

  const user = useCommonStore.use.user();

  const [lang, country, flag] = useMemo(() => {
    if (user?.country == null) return [];

    const lang = getLanguageByCountry(user.country);

    if (lang == null) return [];

    return [lang, countryTitle[lang], getLocaleFlagIcon(lang)];
  }, [user?.country]);

  const handleChangeLanguage = useCallback(async () => {
    if (lang == null) return;

    await changeLanguage(lang);

    onClose();
  }, [onClose, push, pathname, currentLang, lang, changeLanguage]);

  return (
    <ScLocalePopup>
      <div className="img-container">
        <ImageComponent
          className="emoji"
          src="/emojis/thinkingEmoji.webp"
          width={54}
          height={54}
          alt="emoji"
        />
        <ImageComponent
          className="message"
          src="/emojis/message.webp"
          width={42}
          height={42}
          alt="message"
        />
        {flag && (
          <div className="flag-img">
            <ImageComponent
              src={flag}
              width={24}
              height={16}
              alt={`${country} flag`}
            />
          </div>
        )}
      </div>
      <ButtonClose
        className="btn-close"
        gray
        onClick={onClose}
        top={12}
        right={12}
      />
      <div className="text-wrapper">
        <h3 className="title">
          {t('wrongLocale')} {country}.
        </h3>
        <span className="subtitle">{t('switchLocale')}</span>
      </div>
      <Button
        className="button"
        primary
        large
        wide
        onClick={handleChangeLanguage}
      >
        {pending ? (
          <Spinner size={20} color={whiteColor} background={mainColor} center />
        ) : (
          t('yes')
        )}
      </Button>
    </ScLocalePopup>
  );
};
